import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Play, CheckCircle2, Trash2, Trophy, Timer, SprayCan, Users, ListChecks, LayoutDashboard } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { DetailWorkDialog } from '../components/DetailWorkDialog';
import { DetailCelebration } from '../components/DetailCelebration';
import { ExecutedJobs } from './detail/ExecutedJobs';
import { useDeepLink } from '../hooks/useDeepLink';

const PERIODS = [['day', 'Hoje'], ['week', '7 dias'], ['month', 'Mês']];

const fmt = (s) => {
  const v = Math.max(0, Math.floor(s || 0));
  const h = String(Math.floor(v / 3600)).padStart(2, '0');
  const m = String(Math.floor((v % 3600) / 60)).padStart(2, '0');
  const sec = String(v % 60).padStart(2, '0');
  return `${h}:${m}:${sec}`;
};
const short = (s) => (!s ? '—' : `${Math.floor(s / 60)}m${String(Math.floor(s % 60)).padStart(2, '0')}`);

const Metric = ({ label, value, sub, icon: Icon }) => (
  <div className="px-4 py-3">
    <p className="ent-label flex items-center gap-1.5">{Icon && <Icon className="h-3 w-3" />} {label}</p>
    <p className="dt-timer mt-1 text-2xl font-black leading-none text-foreground">{value}</p>
    {sub && <p className="mt-1 text-[11px] text-muted-foreground/70">{sub}</p>}
  </div>
);

const DetailShop = () => {
  const [board, setBoard] = useState({ current: null, queue: [], others: [], done_today: [], stats: {} });
  const [rank, setRank] = useState({ ranking: [] });
  const [period, setPeriod] = useState('day');
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [party, setParty] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('operacao');
  const [focusJob, setFocusJob] = useState('');
  useDeepLink('job', (id) => { setTab('executados'); setFocusJob(id); });

  const load = useCallback(async () => {
    try { const { data } = await api.get('/detail/board'); setBoard(data); } catch (e) {}
  }, []);
  const loadRank = useCallback(async (p) => {
    try { const { data } = await api.get(`/detail/ranking?period=${p}`); setRank(data); } catch (e) {}
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 20000); return () => clearInterval(iv); }, [load]);
  useEffect(() => { loadRank(period); }, [period, loadRank, board.done_today?.length]);
  useEffect(() => { const iv = setInterval(() => setTick((t) => t + 1), 1000); return () => clearInterval(iv); }, []);

  const cur = board.current;
  const elapsed = cur?.started_at ? Math.floor((Date.now() - new Date(cur.started_at).getTime()) / 1000) : 0;

  const start = async (jid) => {
    setBusy(true);
    try { await api.post(`/detail/jobs/${jid}/start`); await load(); toast({ title: '▶ Trabalho iniciado' }); }
    catch (e) { toast({ title: 'Não foi possível iniciar', description: e.response?.data?.detail || '' }); }
    finally { setBusy(false); }
  };

  const finish = async () => {
    if (!cur) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/detail/jobs/${cur.id}/finish`);
      await load(); await loadRank(period);
      if (data.record_broken) setParty(data);
      else toast({ title: '✓ Trabalho finalizado', description: `Tempo ${fmt(data.duration_seconds)} · ${data.today_count} hoje` });
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setBusy(false); }
  };

  const remove = async (jid) => {
    try { await api.delete(`/detail/jobs/${jid}`); load(); } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
  };

  const st = board.stats || {};

  return (
    <div className="space-y-4" data-testid="detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="ent-label">Detail / Clean Department</p>
          <h1 className="font-display text-2xl font-black uppercase tracking-tight">Controle de trabalhos</h1>
        </div>
        <button data-testid="detail-add-work" onClick={() => setOpen(true)} className="ent-btn ent-btn-primary h-9 px-4">
          <Plus className="h-3.5 w-3.5" /> Adicionar trabalho
        </button>
      </div>

      <div className="ent-tabs">
        <button data-testid="detail-tab-operacao" onClick={() => setTab('operacao')}
          className={`ent-tab flex items-center gap-1.5 ${tab === 'operacao' ? 'ent-tab-active' : ''}`}>
          <LayoutDashboard className="h-3.5 w-3.5" /> Operação
        </button>
        <button data-testid="detail-tab-executados" onClick={() => setTab('executados')}
          className={`ent-tab flex items-center gap-1.5 ${tab === 'executados' ? 'ent-tab-active' : ''}`}>
          <ListChecks className="h-3.5 w-3.5" /> Carros executados
        </button>
      </div>

      {tab === 'executados' ? <ExecutedJobs focusId={focusJob} /> : (
      <>
      <div className="ent-panel ent-ribbon" data-testid="detail-metrics">
        <Metric label="Finalizados hoje (você)" value={st.my_today ?? 0} sub={`Loja: ${st.shop_today ?? 0} · 7 dias: ${st.my_week ?? 0}`} icon={SprayCan} />
        <Metric label="Seu recorde diário" value={st.record ?? 0} sub={st.record_day ? st.record_day.split('-').reverse().join('/') : 'sem recorde ainda'} icon={Trophy} />
        <Metric label="Tempo médio hoje" value={short(st.avg_seconds)} sub="por veículo" icon={Timer} />
        <Metric label="Delivery / Entrada" value={`${st.delivery_today ?? 0} / ${st.entrada_today ?? 0}`} sub="finalizados hoje na loja" icon={Users} />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="space-y-4 xl:col-span-2">
          <div className={`ent-panel ${cur ? 'border-primary/60' : ''}`} data-testid="detail-current-panel">
            <div className="ent-panel-head"><span className="ent-label">Trabalho em execução</span></div>
            {cur ? (
              <div className="p-6 sm:p-8">
                <span className="ent-tag">{cur.tipo_label}</span>
                <h2 className="mt-3 font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl" data-testid="detail-current-car">
                  {cur.ano} {cur.marca} {cur.modelo}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">{[cur.trim, cur.cor, cur.carroceria].filter(Boolean).join(' · ') || '—'}</p>
                <p className="dt-timer mt-1 text-xs tracking-[0.14em] text-muted-foreground/70">VIN {cur.vin}</p>
                {cur.notas && <p className="mt-3 border-l-2 border-primary/50 pl-3 text-xs text-muted-foreground">{cur.notas}</p>}
                <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
                  <div>
                    <p className="ent-label dt-live">Tempo em execução</p>
                    <p data-testid="detail-timer" className="dt-timer mt-1 text-6xl font-black leading-none text-primary sm:text-7xl">{fmt(elapsed)}</p>
                  </div>
                  <button data-testid="detail-finish-btn" disabled={busy} onClick={finish}
                    className="ent-btn ent-btn-primary h-14 px-8 text-sm">
                    <CheckCircle2 className="h-5 w-5" /> FINALIZADO
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-10 text-center">
                <SprayCan className="mx-auto h-8 w-8 text-muted-foreground/40" />
                <p className="mt-3 text-sm text-muted-foreground">Nenhum trabalho em execução.</p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {board.queue?.[0] && (
                    <button data-testid="detail-start-next" onClick={() => start(board.queue[0].id)} className="ent-btn ent-btn-primary h-9 px-4">
                      <Play className="h-3.5 w-3.5" /> Iniciar próximo da fila
                    </button>
                  )}
                  <button onClick={() => setOpen(true)} className="ent-btn h-9 px-4"><Plus className="h-3.5 w-3.5" /> Adicionar trabalho</button>
                </div>
              </div>
            )}
          </div>

          <div className="ent-panel" data-testid="detail-queue-panel">
            <div className="ent-panel-head">
              <span className="ent-label">Fila de serviço</span>
              <span className="ent-pill">{board.queue?.length || 0}</span>
            </div>
            {!board.queue?.length ? (
              <p className="ent-empty">Fila vazia</p>
            ) : (
              <table className="ent-table">
                <thead><tr><th>Veículo</th><th>VIN</th><th>Tipo</th><th>Adicionado por</th><th /></tr></thead>
                <tbody>
                  {board.queue.map((j) => (
                    <tr key={j.id} data-testid={`detail-queue-${j.id}`}>
                      <td className="font-semibold">{j.ano} {j.marca} {j.modelo}</td>
                      <td className="dt-timer text-[11px] text-muted-foreground">{j.vin.slice(-8)}</td>
                      <td><span className="ent-tag">{j.tipo_label}</span></td>
                      <td className="text-muted-foreground">{j.created_by_name}</td>
                      <td className="text-right">
                        <div className="flex justify-end gap-1.5">
                          <button data-testid={`detail-start-${j.id}`} disabled={busy} onClick={() => start(j.id)} className="ent-btn ent-btn-sm"><Play className="h-3 w-3" /> Iniciar</button>
                          <button data-testid={`detail-del-${j.id}`} onClick={() => remove(j.id)} className="ent-btn ent-btn-icon"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="ent-panel" data-testid="detail-ranking-panel">
            <div className="ent-panel-head">
              <span className="ent-label">Ranking de produtividade</span>
              <div className="flex gap-1">
                {PERIODS.map(([k, l]) => (
                  <button key={k} data-testid={`detail-rank-${k}`} onClick={() => setPeriod(k)}
                    className={`ent-pill px-2 py-0.5 ${period === k ? 'border-primary text-foreground' : ''}`}>{l}</button>
                ))}
              </div>
            </div>
            {!rank.ranking?.length ? (
              <p className="ent-empty">Sem trabalhos finalizados no período</p>
            ) : (
              <table className="ent-table">
                <thead><tr><th>#</th><th>Profissional</th><th className="text-right">Carros</th><th className="text-right">Média</th></tr></thead>
                <tbody>
                  {rank.ranking.map((r) => (
                    <tr key={r.worker_id || r.position} className={r.is_me ? 'bg-primary/[0.06]' : ''} data-testid={`detail-rank-row-${r.position}`}>
                      <td className="dt-timer font-bold text-muted-foreground">{r.position}</td>
                      <td className="font-semibold">{r.worker_name}{r.is_me && <span className="ent-label ml-1.5 text-primary">você</span>}</td>
                      <td className="dt-timer text-right font-black">{r.jobs}</td>
                      <td className="dt-timer text-right text-muted-foreground">{short(r.avg_seconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {!!board.others?.length && (
            <div className="ent-panel" data-testid="detail-others-panel">
              <div className="ent-panel-head"><span className="ent-label">Em execução por outros</span><span className="ent-pill">{board.others.length}</span></div>
              <div className="divide-y divide-border">
                {board.others.map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold">{o.ano} {o.marca} {o.modelo}</p>
                      <p className="text-[11px] text-muted-foreground">{o.worker_name} · {o.tipo_label}</p>
                    </div>
                    <span className="dt-timer text-xs font-bold text-primary">{fmt(Math.floor((Date.now() - new Date(o.started_at).getTime()) / 1000))}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="ent-panel" data-testid="detail-done-panel">
            <div className="ent-panel-head"><span className="ent-label">Finalizados hoje</span><span className="ent-pill">{board.done_today?.length || 0}</span></div>
            {!board.done_today?.length ? (
              <p className="ent-empty">Nenhum trabalho finalizado hoje</p>
            ) : (
              <div className="max-h-[320px] divide-y divide-border overflow-y-auto">
                {board.done_today.map((j) => (
                  <div key={j.id} className="flex items-center justify-between gap-3 px-3 py-2.5" data-testid={`detail-done-${j.id}`}>
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold">{j.ano} {j.marca} {j.modelo}</p>
                      <p className="text-[11px] text-muted-foreground">{j.worker_name} · {j.tipo_label}</p>
                    </div>
                    <span className="dt-timer text-xs text-muted-foreground">{short(j.duration_seconds)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      </>
      )}

      <DetailWorkDialog open={open} onClose={() => setOpen(false)} onCreated={load} />
      {party && <DetailCelebration data={party} onClose={() => setParty(null)} />}
    </div>
  );
};

export default DetailShop;
